import { ChevronRight, Loader2, Plus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { UserInfo } from '@/services/api/lake'
import { cn } from '@/utils/cn'
import { UserBadges } from './UserBadges'

export interface UserListProps {
  users: UserInfo[]
  selectedUserId: string | null
  loading: boolean
  onSelect: (id: string) => void
  /** Omitted when user creation is unavailable (SSO) — the button is then hidden. */
  onCreateUser?: () => void
}

export function UserList({
  users,
  selectedUserId,
  loading,
  onSelect,
  onCreateUser,
}: UserListProps) {
  const { t } = useTranslation()
  return (
    <Card className="max-md:gap-0! max-md:overflow-hidden max-md:pb-0">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2 text-sm max-md:row-span-2 max-md:self-center">
          <Users className="h-4 w-4" />
          {t('admin.users.usersCount')}
          <span className="text-muted-foreground font-normal">({users.length})</span>
        </CardTitle>
        {onCreateUser && (
          <CardAction>
            <Button size="sm" className="h-9 text-xs md:h-7" onClick={onCreateUser}>
              <Plus className="h-3 w-3 mr-1" />
              {t('admin.users.newUser')}
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Users className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">{t('admin.users.noUsers')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border max-lg:max-h-[45dvh] max-lg:overflow-y-auto">
            {users.map(u => (
              <button
                type="button"
                key={u.id}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                  selectedUserId === u.id ? 'bg-accent' : 'hover:bg-accent/50'
                )}
                onClick={() => onSelect(u.id)}
              >
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground uppercase flex-shrink-0">
                  {u.username[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground truncate">
                      {u.username}
                    </span>
                    <UserBadges user={u} size="sm" />
                  </div>
                  {u.email && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{u.email}</p>
                  )}
                </div>
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-muted-foreground flex-shrink-0 transition-colors',
                    selectedUserId === u.id && 'text-selected-foreground'
                  )}
                />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
